import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Truck, Clock, MapPin, CheckCircle, AlertCircle, Loader2, Package } from "lucide-react";
import api from "@/utils/api";

const SameDayDeliveryCheck = ({ listingId, buyerLocation, onQuoteReceived }) => {
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    if (buyerLocation?.latitude && buyerLocation?.longitude) {
      checkAvailability();
    }
  }, [buyerLocation, listingId]);

  const checkAvailability = async () => {
    if (!buyerLocation?.latitude || !buyerLocation?.longitude) return;
    
    setChecking(true);
    try {
      const res = await api.post(`/delivery/check-availability?listing_id=${listingId}&buyer_latitude=${buyerLocation.latitude}&buyer_longitude=${buyerLocation.longitude}`);
      setAvailability(res.data);
      if (res.data.available && onQuoteReceived) {
        onQuoteReceived(res.data);
      }
    } catch (error) {
      console.error("Failed to check delivery availability:", error);
    } finally {
      setChecking(false);
    }
  };

  if (checking) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking same-day delivery availability...
      </div>
    );
  }

  if (!availability) return null;

  if (availability.available) {
    return (
      <div className="flex items-center gap-2 text-sm text-emerald-600">
        <Truck className="h-4 w-4" />
        <span>Same-day delivery available ({availability.distance_km} km away)</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Truck className="h-4 w-4" />
      <span>Same-day delivery not available for your location</span>
    </div>
  );
};

const SameDayDeliveryOption = ({ listing, user, onDeliveryCreated }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [step, setStep] = useState("address"); // address, quote, confirm
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [availability, setAvailability] = useState(null);
  
  const [deliveryForm, setDeliveryForm] = useState({
    buyer_name: user?.name || "",
    buyer_phone: user?.phone || "",
    buyer_email: user?.email || "",
    buyer_address: "",
    buyer_city: "",
    buyer_state: "",
    buyer_postal_code: "",
    buyer_latitude: null,
    buyer_longitude: null,
    delivery_notes: ""
  });

  // Get user's current location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setDeliveryForm(prev => ({
            ...prev,
            buyer_latitude: position.coords.latitude,
            buyer_longitude: position.coords.longitude
          }));
        },
        (error) => {
          console.log("Geolocation not available:", error);
        }
      );
    }
  }, []);

  const checkAvailability = async () => {
    if (!deliveryForm.buyer_latitude || !deliveryForm.buyer_longitude) {
      toast.error("Please enable location services or enter your coordinates");
      return false;
    }

    setLoading(true);
    try {
      const res = await api.post(`/delivery/check-availability?listing_id=${listing.listing_id}&buyer_latitude=${deliveryForm.buyer_latitude}&buyer_longitude=${deliveryForm.buyer_longitude}`);
      setAvailability(res.data);
      return res.data.available;
    } catch (error) {
      toast.error("Failed to check delivery availability");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getQuote = async () => {
    if (!deliveryForm.buyer_address || !deliveryForm.buyer_city || !deliveryForm.buyer_state || !deliveryForm.buyer_postal_code) {
      toast.error("Please fill in all address fields");
      return;
    }

    const isAvailable = await checkAvailability();
    if (!isAvailable) {
      toast.error("Same-day delivery is not available for your location");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/delivery/quote", {
        listing_id: listing.listing_id,
        buyer_latitude: deliveryForm.buyer_latitude,
        buyer_longitude: deliveryForm.buyer_longitude,
        buyer_address: deliveryForm.buyer_address,
        buyer_city: deliveryForm.buyer_city,
        buyer_state: deliveryForm.buyer_state,
        buyer_postal_code: deliveryForm.buyer_postal_code
      });
      setQuote(res.data);
      setStep("quote");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to get delivery quote");
    } finally {
      setLoading(false);
    }
  };

  const createDelivery = async () => {
    if (!deliveryForm.buyer_name || !deliveryForm.buyer_phone) {
      toast.error("Please provide your name and phone number");
      return;
    }

    setLoading(true);
    try {
      const res = await api.post("/delivery/create", {
        listing_id: listing.listing_id,
        buyer_name: deliveryForm.buyer_name,
        buyer_phone: deliveryForm.buyer_phone,
        buyer_email: deliveryForm.buyer_email,
        buyer_address: deliveryForm.buyer_address,
        buyer_city: deliveryForm.buyer_city,
        buyer_state: deliveryForm.buyer_state,
        buyer_postal_code: deliveryForm.buyer_postal_code,
        buyer_latitude: deliveryForm.buyer_latitude,
        buyer_longitude: deliveryForm.buyer_longitude,
        delivery_notes: deliveryForm.delivery_notes,
        quote_id: quote?.quote_id
      });
      
      toast.success("Same-day delivery requested successfully!");
      setShowDialog(false);
      if (onDeliveryCreated) {
        onDeliveryCreated(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create delivery");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setDeliveryForm(prev => ({ ...prev, [field]: value }));
  };

  // Try to geocode address when all fields are filled
  const geocodeAddress = async () => {
    if (!deliveryForm.buyer_address || !deliveryForm.buyer_city || !deliveryForm.buyer_state) return;
    
    // Use a simple geocoding approach - in production, you'd use Google Maps or similar
    // For now, we'll prompt user to use their device location
    if (!deliveryForm.buyer_latitude && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setDeliveryForm(prev => ({
            ...prev,
            buyer_latitude: position.coords.latitude,
            buyer_longitude: position.coords.longitude
          }));
        },
        () => {
          toast.info("Please enable location services for accurate delivery estimates");
        }
      );
    }
  };

  return (
    <>
      <Card className="p-4 border-emerald-200 bg-emerald-50/50">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Truck className="h-5 w-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-emerald-800">Same-Day Local Delivery</h4>
            <p className="text-sm text-emerald-700 mt-1">
              Get this item delivered to your door today via Burq
            </p>
            {listing?.location?.city && (
              <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Ships from {listing.location.city}
              </p>
            )}
          </div>
          <Button 
            onClick={() => setShowDialog(true)}
            className="bg-emerald-500 hover:bg-emerald-600"
            data-testid="same-day-delivery-btn"
          >
            Get Quote
          </Button>
        </div>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-emerald-500" />
              Same-Day Delivery
            </DialogTitle>
            <DialogDescription>
              {step === "address" && "Enter your delivery address to get a quote"}
              {step === "quote" && "Review your delivery quote"}
              {step === "confirm" && "Confirm your delivery details"}
            </DialogDescription>
          </DialogHeader>

          {step === "address" && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Street Address</Label>
                  <Input
                    placeholder="123 Main St"
                    value={deliveryForm.buyer_address}
                    onChange={(e) => handleInputChange("buyer_address", e.target.value)}
                    onBlur={geocodeAddress}
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    placeholder="Los Angeles"
                    value={deliveryForm.buyer_city}
                    onChange={(e) => handleInputChange("buyer_city", e.target.value)}
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    placeholder="CA"
                    value={deliveryForm.buyer_state}
                    onChange={(e) => handleInputChange("buyer_state", e.target.value)}
                  />
                </div>
                <div className="col-span-2">
                  <Label>ZIP Code</Label>
                  <Input
                    placeholder="90001"
                    value={deliveryForm.buyer_postal_code}
                    onChange={(e) => handleInputChange("buyer_postal_code", e.target.value)}
                  />
                </div>
              </div>

              {!deliveryForm.buyer_latitude && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Enable location services for accurate delivery estimates
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      if (navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition(
                          (pos) => {
                            setDeliveryForm(prev => ({
                              ...prev,
                              buyer_latitude: pos.coords.latitude,
                              buyer_longitude: pos.coords.longitude
                            }));
                            toast.success("Location detected!");
                          },
                          () => toast.error("Could not get your location")
                        );
                      }
                    }}
                  >
                    <MapPin className="h-4 w-4 mr-1" />
                    Use My Location
                  </Button>
                </div>
              )}

              {deliveryForm.buyer_latitude && (
                <div className="p-2 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-sm text-emerald-700 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Location detected
                  </p>
                </div>
              )}

              <Button 
                className="w-full bg-emerald-500 hover:bg-emerald-600"
                onClick={getQuote}
                disabled={loading || !deliveryForm.buyer_address || !deliveryForm.buyer_latitude}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Getting Quote...
                  </>
                ) : (
                  "Get Delivery Quote"
                )}
              </Button>
            </div>
          )}

          {step === "quote" && quote && (
            <div className="space-y-4">
              <Card className="p-4 bg-slate-50">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Item</p>
                    <p className="font-medium">{listing?.title}</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">
                    {quote.distance_km} km
                  </Badge>
                </div>

                <div className="space-y-3 border-t pt-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Est. Delivery Time
                    </span>
                    <span className="font-medium">{quote.estimated_duration_minutes} mins</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Delivery Cost</span>
                    <span className="font-bold text-lg text-emerald-600">
                      ${quote.total_cost.toFixed(2)}
                    </span>
                  </div>
                  {quote.estimated_arrival_time && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Est. Arrival</span>
                      <span className="font-medium">
                        {new Date(quote.estimated_arrival_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </div>
                  )}
                </div>

                {quote.is_mock && (
                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                    * Estimated pricing. Final cost may vary.
                  </div>
                )}
              </Card>

              <div className="space-y-3">
                <div>
                  <Label>Your Name</Label>
                  <Input
                    value={deliveryForm.buyer_name}
                    onChange={(e) => handleInputChange("buyer_name", e.target.value)}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label>Phone Number</Label>
                  <Input
                    value={deliveryForm.buyer_phone}
                    onChange={(e) => handleInputChange("buyer_phone", e.target.value)}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <Label>Delivery Notes (optional)</Label>
                  <Textarea
                    value={deliveryForm.delivery_notes}
                    onChange={(e) => handleInputChange("delivery_notes", e.target.value)}
                    placeholder="Gate code, leave at door, etc."
                    rows={2}
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep("address")} className="flex-1">
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  onClick={createDelivery}
                  disabled={loading || !deliveryForm.buyer_name || !deliveryForm.buyer_phone}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Package className="h-4 w-4 mr-2" />
                      Request Delivery
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export { SameDayDeliveryCheck, SameDayDeliveryOption };
export default SameDayDeliveryOption;
